import { ENumberOfCalls } from '@/_constants/analytics';
import { clearAllTimeouts } from '../clear-all-timeouts/index';

const DEFAULT_DELAY = [0];
const DEFAULT_OPTIONS = { threshold: 0.1 };
const TIME_CHECK_INTERVAL_MS = 1000;

type TParams = {
  element: HTMLDivElement | null;
  callback: ({ duration }: { duration: number }) => void;
  options?: IntersectionObserverInit;
  delay?: Array<number>;
  numberOfCalls?: ENumberOfCalls;
  unobserveCallback?: () => void;
};

type TSetVisibilityTimeoutsProps = {
  delay: Array<number>;
  entryTime: number;
  callback: ({ duration }: { duration: number }) => void;
};

// Sets visibility check timeouts based on specified delay times
// Each timeout checks if the element has been in the viewport long enough
const setVisibilityTimeouts = ({
  delay,
  entryTime,
  callback,
}: TSetVisibilityTimeoutsProps): Array<NodeJS.Timeout> => {
  return delay.map((millisecond) =>
    setTimeout(() => {
      const totalTimeInViewport = performance.now() - entryTime;
      const isTimeInViewportValid =
        totalTimeInViewport >= millisecond &&
        totalTimeInViewport < millisecond + TIME_CHECK_INTERVAL_MS;

      if (isTimeInViewportValid) {
        const duration = millisecond / TIME_CHECK_INTERVAL_MS;
        callback({ duration });
      }
    }, millisecond),
  );
};

// Main function to observe the visibility of the specified element
// It sets timeouts based on visibility changes and clears them if the element is no longer visible.
// The visibility event triggers only once when the element first enters the viewport.
export const watchOfIntersecting = ({
  element,
  callback,
  options = DEFAULT_OPTIONS,
  delay = DEFAULT_DELAY,
  numberOfCalls = ENumberOfCalls.ONCE,
  unobserveCallback,
}: TParams): (() => void) => {
  let timeouts: Array<NodeJS.Timeout> = [];

  if (!element) return () => {};

  const observer = new IntersectionObserver(([entry]) => {
    if (entry?.isIntersecting) {
      timeouts = setVisibilityTimeouts({
        delay,
        entryTime: entry?.time,
        callback,
      });
    } else if (!entry?.isIntersecting && timeouts.length > 0) {
      if (numberOfCalls === ENumberOfCalls.ONCE) {
        observer.unobserve(element);
        unobserveCallback?.();
      }
      clearAllTimeouts(timeouts);
      timeouts = [];
    }
  }, options);

  observer.observe(element);

  return () => {
    observer.disconnect();
    clearAllTimeouts(timeouts);
    timeouts = [];
  };
};
